import type { GetStaticProps } from 'next';
import { useRouter } from 'next/router';
import { NextSeo } from 'next-seo';
import { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';

import { Container, Main } from '@seuk/design-system/atoms';
import { useDebounce } from '@seuk/util';

import { CategoriesList } from '../../components/categories-list';
import { PageHeader } from '../../components/page-header';
import { Search } from '../../components/search';
import { getEntries, toDashed } from '../../modules/page-help/utils';
import type { SearchResult } from '../../services/search';
import { createFakeSearch } from '../../services/search';

const BASE_PATH = '/help/';

const CategoriesSection = styled.section`
  background: white;
  padding: ${({ theme }) => theme.spacing(20)} 0;
`;

const getGroupedCategories = async (): Promise<Category[]> => {
  const entries = await getEntries();

  const categories = entries.reduce((acc, entry) => {
    const category = entry.fields.category;
    if (!acc[category]) {
      acc[category] = new Set();
    }
    acc[category].add(entry.fields.subcategory);
    return acc;
  }, {});

  return Object.entries<Set<string>>(categories).map(([category, childCategories]) => {
    return {
      label: category,
      name: `${BASE_PATH}${toDashed(category)}`,
      childCategories: [...childCategories].map((subcategory) => {
        return {
          label: subcategory,
          name: `${BASE_PATH}${toDashed(subcategory)}`,
          childCategories: [],
        };
      }) as Category[],
    };
  });
};

const getSearchResults = async (): Promise<SearchResult[]> => {
  const entries = await getEntries();
  return entries.map(({ fields: { slug, title, category } }) => {
    return {
      id: slug,
      title,
      url: `${BASE_PATH}${toDashed(category)}/${slug}`,
      category,
    };
  });
};

export interface Category {
  label: string;
  name: string;
  childCategories: Category[];
}

interface IndexProps {
  categories: Category[];
  searchResults: SearchResult[];
}

export default function CategoryGroupPage({ categories, searchResults }: IndexProps) {
  const title = 'Shell Energy Business';

  const router = useRouter();
  // client side help article search
  const useSearch = createFakeSearch(searchResults);
  const [rawSearchQuery, setRawSearchQuery] = useState('');
  const debouncedQuery = useDebounce(rawSearchQuery, 150);
  const searchData = useSearch(debouncedQuery);

  const request = useMemo(
    () =>
      searchData.ready ? { query: searchData.query, error: searchData.error, results: searchData.data } : undefined,
    [searchData]
  );

  const onCategoryClick = useCallback(() => {
    // tracking placeholder
  }, []);

  const redirectTo = useCallback((url: string) => router.push(url), [router]);

  const onSearchSelectItem = useCallback(
    (_query: string, item: SearchResult) => {
      redirectTo(item.url);
    },
    [redirectTo]
  );

  return (
    <>
      <NextSeo title={title} noindex={true} />

      <Main>
        <PageHeader title="How can we help your business today?" />

        <Container>
          <Search
            initialValue={rawSearchQuery}
            onQueryChange={setRawSearchQuery}
            onSelectItem={onSearchSelectItem}
            request={request}
          />
          <CategoriesSection>
            <Container>
              <CategoriesList categories={categories} onCategoryClick={onCategoryClick} />
            </Container>
          </CategoriesSection>
        </Container>
      </Main>
    </>
  );
}

export const getStaticProps: GetStaticProps<IndexProps> = async () => {
  const categories = await getGroupedCategories();
  const searchResults = await getSearchResults();

  return {
    props: { categories, searchResults },
  };
};
